import { useEffect, useRef, useState, useCallback } from 'react';
import { scroller } from 'react-scroll';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { useIntl } from 'react-intl';
import LinesEllipsis from 'react-lines-ellipsis';
import responsiveHOC from 'react-lines-ellipsis/lib/responsiveHOC';
import Image from 'next/image';

import Chip from 'src/components/Chip';
import AddonsConfigurator from 'src/components/AddonsConfigurator';
import Input from 'src/components/Input';

import theme from 'src/styles/components/MenuPositionConfigurator.module.scss';
import { isEmpty } from 'lodash';

const ResponsiveEllipsis = responsiveHOC()(LinesEllipsis);

const MenuPositionConfigurator = ({
    id,
    description,
    imageUrl,
    imageSrcSetUrls,
    isActive,
    tags,
    allergens,
    isCircleMode,
    setSelectedMenuPosition,
    price,
    name,
    menuId,
    category,
    categoryId,
    context,
    fetchMenuPositionDetails,
    configurablePosition,
    changeComment,
    validationErrors,
    isAddPositionPossible
}) => {
    const containerRef = useRef();
    const fillRestRef = useRef();
    const headerContentRef = useRef();
    const imageHeaderRef = useRef();
    const headerRef = useRef();
    const contentRef = useRef();

    const [areOrdersFetched, setOrdersFetched] = useState(false);
    const [isSticky, setSticky] = useState(false);
    const [isHeaderContentSticky, setHeaderContentSticky] = useState(false);

    const menuItem = {
        id,
        description,
        imageUrl,
        imageSrcSetUrls,
        isActive,
        tags,
        allergens,
        isCircleMode,
        price,
        name
    };

    useEffect(() => {
        if (validationErrors.length > 0) {
            setTimeout(() => {
                if (imageUrl) {
                    scroller.scrollTo(validationErrors[0].groupId, {
                        duration: 1000,
                        delay: 0,
                        smooth: 'easeInOutQuart',
                        offset: 100,
                        ignoreCancelEvents: true,
                        containerId: 'menuPositionContainer'
                    });
                } else {
                    scroller.scrollTo(validationErrors[0].groupId, {
                        duration: 1000,
                        delay: 0,
                        smooth: 'easeInOutQuart',
                        offset: -30,
                        ignoreCancelEvents: true,
                        containerId: 'menuPositionContainer'
                    });
                }
            }, 100);
        }
    }, [validationErrors]);

    const calculateMinHeight = useCallback(() => {
        if (!containerRef.current || isEmpty(containerRef.current) || !imageUrl) {
            return;
        }

        const hasScroll = containerRef.current.scrollHeight - containerRef.current.clientHeight > 0;
        const scrollRest = containerRef.current.scrollHeight - fillRestRef.current.clientHeight - containerRef.current.clientHeight;
        const isHeaderGreaterThanScroll = scrollRest < headerRef.current.clientHeight && scrollRest > 0;

        if (hasScroll && isHeaderGreaterThanScroll) {
            fillRestRef.current.style.height = headerContentRef.current.clientHeight > imageHeaderRef.current.clientHeight
                ? `${headerContentRef.current.clientHeight - scrollRest + 60}px`
                : `${headerRef.current.clientHeight - 60}px`;
        } else {
            fillRestRef.current.style.height = '0px';
        }
    }, [containerRef.current]);

    const onScroll = () => {
        setSticky(containerRef.current.scrollTop >= 30);
        setHeaderContentSticky(containerRef.current.scrollTop >= 150);
    };

    useEffect(() => {
        if (!containerRef.current || isEmpty(containerRef.current)) {
            return;
        }

        if (!areOrdersFetched) {
            setSelectedMenuPosition(menuItem);
            fetchMenuPositionDetails({
                context,
                categoryId,
                menuId,
                positionId: menuItem.id
            });

            setOrdersFetched(true);
        }

        containerRef.current.addEventListener('scroll', onScroll, false);
        window.addEventListener('resize', calculateMinHeight, false);

        const observer = new ResizeObserver(() => {
            calculateMinHeight();
        });

        observer.observe(contentRef.current);

        return () => {
            if (containerRef.current) {
                containerRef.current.removeEventListener('scroll', onScroll);
            }

            window.removeEventListener('resize', onScroll, false);

            observer.disconnect();
        };
    }, [containerRef.current]);

    const intl = useIntl();

    return (
        <div
          id="menuPositionContainer"
          className={classnames(theme.container, {
                [theme.active]: isActive,
                [theme.hasNoPhoto]: !imageUrl
            })}
          ref={containerRef}
          body-scroll-lock-ignore=""
        >
            <div
              className={classnames(theme.header, {
                    [theme.sticky]: isSticky,
                    [theme.headerContentSticky]: isHeaderContentSticky
                })}
              ref={headerRef}
            >
                {imageUrl && (
                    <div className={classnames(theme.photoContainer, { [theme.circle]: isCircleMode })}>
                        <Image
                          className={theme.photo}
                          alt={name}
                          src={imageUrl}
                          layout="fill"
                          objectFit="cover"
                          srcset={imageSrcSetUrls}
                          unoptimized="true"
                        />
                    </div>
                )}
            </div>
            <div
              className={theme.headerContent}
              ref={headerContentRef}
            >
                <h3 className={theme.subtitle}>{category}</h3>
                <div className={theme.headerTitleContainer}>
                    <h2 className={theme.title}>
                        <ResponsiveEllipsis
                          text={name}
                          maxLine="2"
                          ellipsis="..."
                          trimRight
                          basedOn="letters"
                        />
                    </h2>
                    <div className={theme.price}>
                        {intl.formatNumber(price, { style: 'currency', currency: 'PLN' })}
                    </div>
                </div>
            </div>
            <div className={theme.contentContainer}>
                <div
                  className={theme.content}
                  ref={contentRef}
                >
                    <div className={theme.description}>
                        {description}
                    </div>
                    {allergens && (
                        <div className={theme.allergens}>
                            <b>{intl.formatMessage({ id: 'App.allergens' })}:</b> {allergens.join(', ')}
                        </div>
                    )}
                    {tags && (
                        <div className={theme.tags}>
                            {tags.map((tag) => <Chip isDark key={tag}>{tag}</Chip>)}
                        </div>
                    )}
                    {(configurablePosition && configurablePosition.addonGroups) && configurablePosition.addonGroups.map((addonGroup) => (
                        <AddonsConfigurator key={addonGroup.id} {...addonGroup} />
                    ))}
                    {(configurablePosition && isAddPositionPossible) && (
                        <div>
                            <h2 className={theme.categoryName}>
                                {intl.formatMessage({ id: 'App.commentToOrder' })}
                            </h2>
                            <Input
                              className={theme.commentInput}
                              multiline
                              theme={theme}
                              value={configurablePosition.comment}
                              onChange={changeComment}
                              rows={5}
                            />
                        </div>
                    )}
                </div>
                <div className={theme.fillRest} ref={fillRestRef} />
            </div>
        </div>
    );
};

MenuPositionConfigurator.propTypes = {
    id: PropTypes.string.isRequired,
    description: PropTypes.string,
    price: PropTypes.number.isRequired,
    imageUrl: PropTypes.string,
    imageSrcSetUrls: PropTypes.string,
    name: PropTypes.string,
    isActive: PropTypes.bool,
    tags: PropTypes.arrayOf(PropTypes.string),
    allergens: PropTypes.arrayOf(PropTypes.string),
    isCircleMode: PropTypes.bool,
    fetchMenuPositionDetails: PropTypes.func.isRequired,
    setSelectedMenuPosition: PropTypes.func.isRequired,
    menuId: PropTypes.string.isRequired,
    categoryId: PropTypes.string.isRequired,
    category: PropTypes.string.isRequired,
    context: PropTypes.shape({
        restaurantId: PropTypes.string.isRequired
    }),
    configurablePosition: PropTypes.shape({
        addonGroups: PropTypes.arrayOf(PropTypes.shape({
            addons: PropTypes.arrayOf(PropTypes.shape({
                addonGroups: PropTypes.arrayOf(PropTypes.object),
                id: PropTypes.string.isRequired,
                maxQuantity: PropTypes.number.isRequired,
                minQuantity: PropTypes.number.isRequired,
                name: PropTypes.string.isRequired,
                parentProductId: PropTypes.string.isRequired,
                price: PropTypes.number.isRequired,
                productId: PropTypes.string.isRequired
            })),
            id: PropTypes.string.isRequired,
            minQuantity: PropTypes.number.isRequired,
            maxQuantity: PropTypes.number.isRequired,
            name: PropTypes.string.isRequired
        })),
        comment: PropTypes.string
    }),
    changeComment: PropTypes.func.isRequired,
    validationErrors: PropTypes.arrayOf(PropTypes.object),
    isAddPositionPossible: PropTypes.bool
};

MenuPositionConfigurator.defaultProps = {
    isCircleMode: false,
    isActive: true,
    validationErrors: []
};

export default MenuPositionConfigurator;
